/**
 * fp-delivery.js — one delivery-date engine for the product page line
 * (snippets/fp-delivery-line.liquid) and the cart drawer row
 * (snippets/fp-cart-delivery.liquid). Replaces fp-cart-delivery.js and the
 * engine that lived in the PDP Custom Liquid block (2026-10-25).
 *
 * Ops facts (founder-confirmed 2026-06-12): orders are picked up by 3:00 PM
 * IST, Mon–Sat (never Sunday); deliveries run all 7 days. Delivery = pickup
 * day + the pincode's transit days.
 *
 * Serviceability is asked LIVE of easyship (2026-11-01), through the store's
 * App Proxy (/apps/easyship → easyship /api/serviceability): every courier we
 * book (Blue Dart and Delhivery), from easyship's nightly pincode masters, in
 * ~0.3 s. The old Blue Dart-only file (assets/pincode-tat.js, June 2026) is
 * now only the fallback when easyship can't answer in time, and a pincode
 * missing from it then reads "couldn't check", never "we don't deliver":
 * Delhivery may well take it.
 *
 * With no pincode the date is pickup + 3 days: the order-weighted median
 * (54% of orders arrive in 3 days, ~76% on or before it).
 *
 * localStorage:
 *   fp_delivery_pincode        the shopper's pincode, shared site-wide
 *   fp_delivery_pin_dismissed  a customer-address pincode they changed away from
 *   fp_delivery_live           { pincode: transit days } (0 = not serviceable),
 *                              easyship answers only; read at first paint by
 *                              the PDP line and the cart-summary enhancer
 *   fp_delivery_live_at        { pincode: ms } when each was asked; older than
 *                              FRESH_MS is shown, then asked again
 * The old fp_delivery_tat_<v> keys (file answers, some wrong) are removed.
 *
 * Checks report through window.fpTrack (GA4 + Clarity): fp_pincode_check and
 * fp_pincode_not_serviceable, with fp_source 'pdp' or 'drawer' and fp_via
 * 'live', 'cache' or 'list' (the fallback file). The drawer also keeps its
 * fp_cart_pincode_change event.
 *
 * Loaded (defer) by sections/fp-cart-drawer.liquid on every page, and again by
 * the PDP line; the guard below makes the second copy a no-op.
 */
(function () {
  if (window.FPDelivery) return;

  var me = document.currentScript;
  var TAT_URL = (me && me.getAttribute('data-tat')) || '';
  var API_URL = (me && me.getAttribute('data-api')) || '/apps/easyship';
  var PIN_KEY = 'fp_delivery_pincode';
  var DISMISS_KEY = 'fp_delivery_pin_dismissed';
  var LIVE_KEY = 'fp_delivery_live';
  var LIVE_AT_KEY = 'fp_delivery_live_at';
  var FRESH_MS = 12 * 3600 * 1000; /* re-ask a remembered pincode twice a day */
  var LIVE_TIMEOUT_MS = 2500; /* past this, fall back to the file */
  var KEEP_PINS = 20;
  var IST_OFFSET_MS = 19800000; /* UTC+5:30, no DST */
  var CUTOFF_HOUR = 15; /* 3 PM IST pickup cutoff */
  var DEFAULT_TRANSIT = 3;
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* IST wall clock carried in a Date's UTC fields (read only via getUTC*), so
     the device's own timezone never matters. Pickup is today before 3 PM,
     otherwise tomorrow, and never a Sunday. */
  function pickupDate() {
    var p = new Date(Date.now() + IST_OFFSET_MS);
    if (p.getUTCDay() === 0 || p.getUTCHours() >= CUTOFF_HOUR) p.setUTCDate(p.getUTCDate() + 1);
    while (p.getUTCDay() === 0) p.setUTCDate(p.getUTCDate() + 1);
    return p;
  }
  function dateLabel(transitDays) {
    var d = pickupDate();
    d.setUTCDate(d.getUTCDate() + transitDays);
    return DAYS[d.getUTCDay()] + ', ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()];
  }

  function isValidPin(v) { return /^[1-9][0-9]{5}$/.test(v || ''); }
  function read(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function write(key, v) {
    try { if (v == null) localStorage.removeItem(key); else localStorage.setItem(key, v); } catch (e) {}
  }
  function savedPin() { var p = read(PIN_KEY); return isValidPin(p) ? p : null; }

  /* easyship's answers, per pincode: 1-15 = days, 0 = not serviceable.
     Anything else is treated as poisoned and asked again. We keep the
     integer, never the date. */
  function readMap(key) {
    try { var m = JSON.parse(read(key) || '{}'); return (m && typeof m === 'object') ? m : {}; } catch (e) { return {}; }
  }
  function cachedDays(pin) {
    var v = readMap(LIVE_KEY)[pin];
    return (typeof v === 'number' && v === Math.floor(v) && v >= 0 && v <= 15) ? v : null;
  }
  function isFresh(pin) {
    var at = readMap(LIVE_AT_KEY)[pin];
    return cachedDays(pin) !== null && typeof at === 'number' && Date.now() - at < FRESH_MS;
  }
  function cacheDays(pin, days) {
    var c = readMap(LIVE_KEY);
    var at = readMap(LIVE_AT_KEY);
    c[pin] = days;
    at[pin] = Date.now();
    /* Keep the newest KEEP_PINS so the maps can't grow without end. */
    var pins = Object.keys(at).sort(function (a, b) { return at[b] - at[a]; });
    pins.slice(KEEP_PINS).forEach(function (p) { delete c[p]; delete at[p]; });
    Object.keys(c).forEach(function (p) { if (!(p in at)) delete c[p]; });
    write(LIVE_KEY, JSON.stringify(c));
    write(LIVE_AT_KEY, JSON.stringify(at));
  }
  function dropOldCaches() {
    try {
      var old = [];
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (k && k.indexOf('fp_delivery_tat_') === 0) old.push(k);
      }
      old.forEach(function (k) { localStorage.removeItem(k); });
    } catch (e) {}
  }

  function isSlowConn() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(c && (c.saveData === true || /(^|-)2g$|^3g$/i.test(c.effectiveType || '')));
  }

  /* The fallback file (~115 KB, ~30 KB gzipped) loads only when easyship
     couldn't answer. */
  var loading = false;
  var waiting = [];
  function withData(cb) {
    if (window.FP_PINCODE_TAT) return cb(true);
    if (!TAT_URL) return cb(false);
    waiting.push(cb);
    if (loading) return;
    loading = true;
    var s = document.createElement('script');
    s.src = TAT_URL;
    s.onload = function () { loading = false; flush(!!window.FP_PINCODE_TAT); };
    s.onerror = function () { loading = false; flush(false); };
    document.head.appendChild(s);
  }
  function flush(ok) {
    var q = waiting;
    waiting = [];
    q.forEach(function (fn) { fn(ok); });
  }

  /* Ask easyship. cb(days): > 0 deliverable, 0 not serviceable, null it
     couldn't answer (timeout, error, 503, anything unexpected). A 503 is
     easyship saying a courier didn't answer: never "not serviceable". */
  function askLive(pin, cb) {
    var done = false;
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    function finish(days) {
      if (done) return;
      done = true;
      clearTimeout(timer);
      cb(days);
    }
    var timer = setTimeout(function () {
      if (ctrl) ctrl.abort();
      finish(null);
    }, LIVE_TIMEOUT_MS);
    try {
      fetch(API_URL + '?pincode=' + pin, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: ctrl ? ctrl.signal : undefined
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (!j || j.pincode !== pin || typeof j.serviceable !== 'boolean') return finish(null);
          if (!j.serviceable) return finish(0);
          var d = j.days;
          finish(typeof d === 'number' && d === Math.floor(d) && d >= 1 && d <= 15 ? d : DEFAULT_TRANSIT);
        })
        .catch(function () { finish(null); });
    } catch (e) {
      finish(null);
    }
  }

  /* cb(days, via): days as askLive; via 'cache' | 'live' | 'list' (the
     fallback file). A fresh remembered answer is instant; otherwise easyship
     is asked, then the remembered answer however old, then the file, which
     can only say yes: a pincode it lacks is "couldn't check" (null). */
  var asking = {};
  function lookup(pin, cb) {
    if (isFresh(pin)) return cb(cachedDays(pin), 'cache');
    if (asking[pin]) return asking[pin].push(cb);
    asking[pin] = [cb];
    function reply(days, via) {
      var q = asking[pin] || [];
      delete asking[pin];
      q.forEach(function (fn) { fn(days, via); });
    }
    askLive(pin, function (days) {
      if (days !== null) {
        cacheDays(pin, days);
        return reply(days, 'live');
      }
      var stale = cachedDays(pin);
      if (stale !== null) return reply(stale, 'cache');
      withData(function (ok) {
        var d = ok ? window.FP_PINCODE_TAT[pin] : null;
        reply(typeof d === 'number' && d >= 1 ? d : null, 'list');
      });
    });
  }

  /* Once per pincode per source per page; only for checks the shopper makes. */
  var tracked = {};
  function track(source, pin, days, via) {
    var ok = days > 0;
    try {
      if (!ok && typeof window.clarity === 'function') window.clarity('set', 'fp_delivery', 'not_serviceable');
      if (tracked[source + pin] || typeof window.fpTrack !== 'function') return;
      tracked[source + pin] = true;
      window.fpTrack('fp_pincode_check', { fp_source: source, fp_pincode: pin, fp_serviceable: ok, fp_via: via || '' });
      if (!ok) window.fpTrack('fp_pincode_not_serviceable', { fp_source: source, fp_pincode: pin, fp_via: via || '' });
    } catch (e) {}
  }

  function idle(fn) { (window.requestIdleCallback || function (f) { return setTimeout(f, 1200); })(fn); }

  /* ---------------------------------------------------------------------
     Product page line (snippets/fp-delivery-line.liquid):
       FREE delivery by Thu, 29 Oct   Check pincode
       FREE delivery to 400001 by Thu, 29 Oct   Change
       We don't deliver to 790001 yet   Change
     The snippet's inline first-paint script mirrors renderLine(); keep the
     two in sync.
     --------------------------------------------------------------------- */
  function linePart(el, name) { return el.querySelector('[data-fp-dline-' + name + ']'); }

  function renderLine(el, pin, days) {
    var free = el.hasAttribute('data-free');
    var html;
    if (pin && days === 0) {
      html = 'We don’t deliver to <strong>' + pin + '</strong> yet';
    } else if (pin) {
      html = (free ? 'FREE delivery' : 'Delivery') + ' to ' + pin + ' by <strong>' + dateLabel(days || DEFAULT_TRANSIT) + '</strong>';
    } else {
      html = (free ? 'FREE delivery' : 'Get it') + ' by <strong>' + dateLabel(DEFAULT_TRANSIT) + '</strong>';
    }
    var text = linePart(el, 'text');
    if (text && el.getAttribute('data-painted') !== html) {
      text.innerHTML = html;
      el.setAttribute('data-painted', html);
    }
    var action = linePart(el, 'action');
    if (action) action.textContent = pin ? 'Change' : 'Check pincode';
    el.setAttribute('data-state', !pin ? 'default' : days === 0 ? 'none' : 'ok');
  }

  function showLineMsg(el, text) {
    var msg = linePart(el, 'msg');
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
  }

  function openLineForm(el) {
    var form = linePart(el, 'form');
    var input = linePart(el, 'input');
    var check = linePart(el, 'check');
    if (!form || !input) return;
    form.hidden = false;
    input.value = '';
    if (check) { check.disabled = true; check.textContent = 'Check'; }
    showLineMsg(el, '');
    input.focus();
  }

  function submitLine(el, pin) {
    var check = linePart(el, 'check');
    el.fpPending = pin;
    if (check && !isFresh(pin)) {
      check.disabled = true;
      check.textContent = 'Checking…';
    }
    lookup(pin, function (days, via) {
      if (el.fpPending !== pin) return; /* a newer pincode was typed */
      if (check) check.textContent = 'Check';
      if (days === null) {
        if (check) check.disabled = false;
        showLineMsg(el, 'Couldn’t check right now. The date above is our usual delivery time.');
        return;
      }
      write(PIN_KEY, pin);
      var form = linePart(el, 'form');
      if (form) form.hidden = true;
      showLineMsg(el, '');
      track('pdp', pin, days, via);
      paintAll();
      var action = linePart(el, 'action');
      if (action) action.focus();
    });
  }

  /* Saved pincode first, then a logged-in customer's address pincode (unless
     they changed away from it), which then becomes their pincode site-wide.
     No IP or GPS guessing: Indian mobile carriers geolocate to a few metro
     pools, so the date would be confidently wrong. */
  function initLine(el) {
    if (el.fpInit) return;
    el.fpInit = true;
    var pin = savedPin();
    var zip = (el.getAttribute('data-zip') || '').replace(/\D/g, '');
    if (!pin && isValidPin(zip) && zip !== read(DISMISS_KEY)) {
      write(PIN_KEY, zip);
      pin = zip;
    }
    var days = pin ? cachedDays(pin) : null;
    renderLine(el, pin, days);
    /* Not asked yet, or asked over FRESH_MS ago: ask when the page is idle,
       except on slow connections, where what we have stays until the
       shopper checks. */
    if (!pin || isFresh(pin) || isSlowConn()) return;
    idle(function () {
      var input = linePart(el, 'input');
      if (input && (document.activeElement === input || input.value)) return; /* never fight the shopper */
      lookup(pin, function (d) { if (d !== null) paintAll(); });
    });
  }

  /* ---------------------------------------------------------------------
     Cart drawer row (snippets/fp-cart-delivery.liquid, locked design "1a"):
       Delivery by Thu, 29 Oct · ADD PINCODE
       Delivering to 400001 · by Wed, 28 Oct · CHANGE
       We don't deliver to 790001 yet · CHANGE
     --------------------------------------------------------------------- */
  var STRONG = '<span class="fp-cart-delivery__strong">';
  var resolving = {};

  function paintRow(root) {
    var pin = savedPin();
    var days = pin ? cachedDays(pin) : null;
    var html;
    if (!pin) html = 'Delivery by ' + STRONG + dateLabel(DEFAULT_TRANSIT) + '</span>';
    else if (days === 0) html = 'We don’t deliver to ' + STRONG + pin + '</span> yet';
    else html = 'Delivering to ' + STRONG + pin + '</span> · by ' + STRONG + dateLabel(days || DEFAULT_TRANSIT) + '</span>';
    /* Change-guarded: paint runs from a MutationObserver, so rewriting the
       same markup would re-trigger it forever. Compare against what we last
       wrote, not innerHTML (browsers normalise it). */
    var text = root.querySelector('[data-fp-delivery-text]');
    if (text && root.getAttribute('data-painted') !== html) {
      text.innerHTML = html;
      root.setAttribute('data-painted', html);
    }
    var btn = root.querySelector('[data-fp-delivery-change]');
    var label = pin ? 'Change' : 'Add pincode';
    if (btn && btn.textContent !== label) btn.textContent = label;
    if (root.hidden) root.hidden = false;
    if (pin && !isFresh(pin) && !resolving[pin] && !isSlowConn()) {
      resolving[pin] = true;
      idle(function () { lookup(pin, function (d) { if (d !== null) paintAll(); }); });
    }
  }

  function setRowEditing(root, editing) {
    root.querySelector('[data-fp-delivery-show]').hidden = editing;
    root.querySelector('[data-fp-delivery-edit]').hidden = !editing;
  }

  function commitRow(root) {
    var input = root.querySelector('[data-fp-delivery-input]');
    var check = root.querySelector('[data-fp-delivery-check]');
    if (!input) return;
    var v = input.value.replace(/\D/g, '').slice(0, 6);
    if (!isValidPin(v)) { input.focus(); return; }
    root.fpPending = v;
    if (check && !isFresh(v)) check.textContent = 'Checking…';
    lookup(v, function (days, via) {
      if (root.fpPending !== v) return;
      if (check) check.textContent = 'Check';
      write(PIN_KEY, v);
      paintAll();
      setRowEditing(root, false);
      if (days !== null) track('drawer', v, days, via);
      try {
        if (typeof window.fpTrack === 'function') {
          window.fpTrack('fp_cart_pincode_change', { fp_pin: v, fp_serviceable: days === null ? '' : days > 0 });
        }
      } catch (e) {}
    });
  }

  function paintAll() {
    document.querySelectorAll('[data-fp-delivery]').forEach(paintRow);
    if (!document.getElementById('fp-delivery')) {
      document.querySelectorAll('[data-fp-dline]').forEach(function (el) {
        if (!el.fpInit) return initLine(el);
        var pin = savedPin();
        renderLine(el, pin, pin ? cachedDays(pin) : null);
      });
    }
  }

  /* All interaction is DELEGATED at document level: section morphs can
     replace either UI's DOM at any time and nothing needs re-wiring. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var line = t.closest('[data-fp-dline]');
    if (line) {
      if (t.closest('[data-fp-dline-action]')) {
        var shown = line.getAttribute('data-state') !== 'default';
        if (shown) {
          /* Changing away from a pincode: forget it, and remember the
             dismissal so a customer-address pincode isn't re-applied. */
          write(DISMISS_KEY, savedPin() || '');
          write(PIN_KEY, null);
          renderLine(line, null, null);
          document.querySelectorAll('[data-fp-delivery]').forEach(paintRow);
        }
        openLineForm(line);
      } else if (t.closest('[data-fp-dline-check]')) {
        var input = linePart(line, 'input');
        if (input && isValidPin(input.value)) submitLine(line, input.value);
      }
      return;
    }

    var root = t.closest('[data-fp-delivery]');
    if (!root) return;
    if (t.closest('[data-fp-delivery-change]')) {
      var field = root.querySelector('[data-fp-delivery-input]');
      if (field) field.value = savedPin() || '';
      setRowEditing(root, true);
      if (field) field.focus();
    } else if (t.closest('[data-fp-delivery-cancel]')) {
      setRowEditing(root, false);
    } else if (t.closest('[data-fp-delivery-check]')) {
      commitRow(root);
    }
  }, false);

  /* No maxlength on either field: the browser would clip a pasted "395 003"
     to "395 00" before this clean-up runs. */
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var lineInput = t.closest('[data-fp-dline-input]');
    var rowInput = t.closest('[data-fp-delivery-input]');
    if (!lineInput && !rowInput) return;
    var clean = t.value.replace(/\D/g, '').slice(0, 6);
    if (t.value !== clean) t.value = clean;
    if (lineInput) {
      var line = t.closest('[data-fp-dline]');
      var check = linePart(line, 'check');
      if (check) check.disabled = !isValidPin(clean);
      showLineMsg(line, '');
      if (isValidPin(clean)) submitLine(line, clean); /* six digits = check, no tap needed */
    }
  }, false);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target || !e.target.closest) return;
    var rowInput = e.target.closest('[data-fp-delivery-input]');
    if (rowInput) {
      e.preventDefault();
      commitRow(rowInput.closest('[data-fp-delivery]'));
    } else if (e.target.closest('[data-fp-dline-input]')) {
      e.preventDefault();
      var line = e.target.closest('[data-fp-dline]');
      if (isValidPin(e.target.value)) submitLine(line, e.target.value);
    }
  }, false);

  /* ZERO-FLICKER REPAINT (kept from fp-cart-delivery.js, 2026-07-12): every
     cart morph re-renders the drawer row with the server's `hidden` attr, so
     the paint must run synchronously in the same cart:update dispatch, i.e.
     this listener must register AFTER cart-items-component's morphing
     listener. The drawer section loads this file as a defer script after
     component-cart-items.js, which guarantees that. The observer stays as a
     backstop for the page-load hydration morph. */
  function start() {
    dropOldCaches();
    document.addEventListener('cart:update', paintAll, false);
    paintAll();
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    var t2 = null;
    new MutationObserver(function () {
      if (t2) clearTimeout(t2);
      t2 = setTimeout(paintAll, 250);
    }).observe(drawer, { childList: true, subtree: true });
  }

  window.FPDelivery = {
    dateLabel: dateLabel,
    savedPin: savedPin,
    cachedDays: cachedDays,
    lookup: lookup,
    paint: paintAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
